import { useEffect, useRef, useState } from 'react'
import { type AIMessage } from '@dataTypes/aiAssist.type'
import {
  postAssistantMessage,
  type AssistantAction,
  type AssistantTurn,
  type AssistantCitation,
} from '@services/api/aiAssistant.api'
import { loadTodaySchedule } from './aiAssistSchedule.util'

export type AssistantMessage = AIMessage & {
  citations?: AssistantCitation[]
  action?: AssistantAction
  source?: 'schedule'
  failed?: boolean
}

type Session = {
  id: string
  title: string
  messages: AssistantMessage[]
  timestamp: Date
}

const stopPendingMessages = (
  messages: AssistantMessage[],
): AssistantMessage[] =>
  messages.map((item) =>
    item.isTyping
      ? {
          ...item,
          content: 'Stopped waiting for this reply.',
          isTyping: false,
          failed: true,
        }
      : item,
  )

export const buildAssistantHistory = (
  messages: AssistantMessage[],
  message: string,
): AssistantTurn[] => {
  const history = messages
    .filter((item) => !item.failed && !item.isTyping && !item.source)
    .slice(-6)
    .map(
      (item): AssistantTurn => ({
        role: item.type === 'user' ? 'user' : 'assistant',
        content: item.content.slice(0, 3000),
      }),
    )
  while (
    history.length &&
    JSON.stringify({ conversation: history, latest_message: message }).length >
      7000
  ) {
    history.shift()
  }
  return history
}

export const assistantErrorMessage = (error: unknown): string => {
  const response = (
    error as { response?: { status?: number; data?: { error?: string } } }
  )?.response
  if (response?.status === 403)
    return 'Your account does not have AI access to this clinic.'
  if (response?.status === 401)
    return 'Your session expired. Please sign in again.'
  if (response?.status === 429)
    return 'AI request limit reached. Please try again later.'
  if (response?.data?.error === 'CONTENT_SAFETY_BLOCKED')
    return 'This content was blocked by safety checks. Edit your message and try again.'
  return 'AI Assistant is temporarily unavailable. Please try again.'
}

type AssistantConversation = {
  messages: AssistantMessage[]
  sessions: Session[]
  sessionId: string
  isLoading: boolean
  send: (
    message: string,
    action?: AssistantAction,
    editId?: string,
    schedule?: boolean,
  ) => Promise<void>
  cancel: () => void
  retry: () => void
  newSession: () => void
  selectSession: (id: string) => void
  deleteSession: (id: string) => void
}

export const useAssistantConversation = (
  clinicId: string,
): AssistantConversation => {
  const [sessions, setSessions] = useState<Session[]>([])
  const [sessionId, setSessionId] = useState(() => crypto.randomUUID())
  const [messages, setMessages] = useState<AssistantMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const requestRef = useRef<AbortController | null>(null)
  const messagesRef = useRef(messages)
  messagesRef.current = messages

  useEffect(() => () => requestRef.current?.abort(), [])

  useEffect(() => {
    if (!messages.length) return
    setSessions((previous) => {
      const session = {
        id: sessionId,
        timestamp: new Date(),
        title: messages[0].content.slice(0, 40),
        messages,
      }
      return [
        session,
        ...previous.filter((item) => item.id !== sessionId),
      ].slice(0, 10)
    })
  }, [messages, sessionId])

  const cancel = (): void => {
    requestRef.current?.abort()
    requestRef.current = null
    setIsLoading(false)
    setMessages(stopPendingMessages)
    setSessions((previous) =>
      previous.map((item) =>
        item.id === sessionId
          ? { ...item, messages: stopPendingMessages(item.messages) }
          : item,
      ),
    )
  }

  const send = async (
    message: string,
    action: AssistantAction = 'chat',
    editId?: string,
    schedule = false,
  ): Promise<void> => {
    if (
      requestRef.current ||
      !clinicId ||
      !message.trim() ||
      message.length > 3000
    )
      return
    const editIndex = editId
      ? messagesRef.current.findIndex((item) => item.id === editId)
      : -1
    const previous =
      editIndex >= 0
        ? messagesRef.current.slice(0, editIndex)
        : messagesRef.current
    const controller = new AbortController()
    requestRef.current = controller
    setIsLoading(true)
    const source = schedule ? ('schedule' as const) : undefined
    const user: AssistantMessage = {
      id: crypto.randomUUID(),
      type: 'user',
      content: message.trim(),
      action,
      source,
    }
    const pending: AssistantMessage = {
      id: crypto.randomUUID(),
      type: 'ai',
      content: schedule
        ? 'Loading today’s schedule…'
        : 'Generating a response…',
      isTyping: true,
      source,
    }
    setMessages([...previous, user, pending].slice(-100))
    try {
      const result = schedule
        ? await loadTodaySchedule()
        : await postAssistantMessage(
            {
              clinic_id: clinicId,
              request_id: crypto.randomUUID(),
              action,
              message: user.content,
              history:
                action === 'chat'
                  ? buildAssistantHistory(previous, user.content)
                  : [],
            },
            controller.signal,
          )
      if (controller.signal.aborted || requestRef.current !== controller) return
      setMessages((current) =>
        current.map((item) =>
          item.id === pending.id
            ? {
                ...item,
                isTyping: false,
                content: 'summary' in result ? result.summary : result.content,
                citations: 'citations' in result ? result.citations : undefined,
                scheduleList:
                  'scheduleList' in result ? result.scheduleList : undefined,
              }
            : item,
        ),
      )
    } catch (error) {
      if (controller.signal.aborted || requestRef.current !== controller) return
      setMessages((current) =>
        current.map((item) =>
          item.id === pending.id
            ? {
                ...item,
                isTyping: false,
                failed: true,
                content: schedule
                  ? 'Unable to load today’s schedule. Please try again or open the calendar.'
                  : assistantErrorMessage(error),
              }
            : item,
        ),
      )
    } finally {
      if (requestRef.current === controller) {
        requestRef.current = null
        setIsLoading(false)
      }
    }
  }

  const newSession = (): void => {
    cancel()
    setSessionId(crypto.randomUUID())
    setMessages([])
  }
  const selectSession = (id: string): void => {
    if (id === sessionId) return
    const session = sessions.find((item) => item.id === id)
    if (!session) return
    cancel()
    setSessionId(id)
    setMessages(stopPendingMessages(session.messages))
  }
  const deleteSession = (id: string): void => {
    if (sessionId === id) newSession()
    setSessions((previous) => previous.filter((item) => item.id !== id))
  }
  const retry = (): void => {
    const user = [...messages].reverse().find((item) => item.type === 'user')
    if (user)
      void send(user.content, user.action, user.id, user.source === 'schedule')
  }

  return {
    messages,
    sessions,
    sessionId,
    isLoading,
    send,
    cancel,
    retry,
    newSession,
    selectSession,
    deleteSession,
  }
}
